import { apiClient } from "@/lib/api-client"
import {
  EmployeeDto,
  EmployeeStatusDto,
  toEmployeeStatusCode,
} from "@/modules/onboarding/employee/dtos"
import { EmployeeDetailsDto } from "@/modules/onboarding/employee/dtos/employee-details.dto"

export interface SageVipEmployeeExportData {
  employeeId: string
  employeeCode: string
  isLearner: boolean
}

function triggerCsvDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
export interface Sage300EmployeePayload {
  // Required
  companyCode: string
  companyRuleCode: string
  employeeCode: string
  genEntityTypeCode: string
  genEntitySubTypeCode: string
  firstName: string
  lastName: string
  dateEngaged: string
  natureOfContractCode: string
  uifStatusCode: string
  leavePolicyCode: string
  remunerationEarnDefCode: string
  paymentRunDefCode: string
  taxStatusCode: string
  taxCalculation: string
  taxStartDate: string
  // Optional
  entityCode?: string
  titleTypeCode?: string
  gender?: string
  initials?: string
  secondName?: string
  otherNames?: string
  knownAsName?: string
  maritalStatusTypeCode?: string
  birthDate?: string
  birthCertificateNumber?: string
  cityOfBirth?: string
  countryOfBirth?: string
  disabled?: boolean
  disabilityType?: string
  ethnicTypeCode?: string
  languageTypeCode?: string
  nationalityCountryCode?: string
  racialGroup?: string
  raceGroup?: string
  idNumber?: string
  passportNo?: string
  passportCountryCode?: string
  religionCode?: string
  bloodGroup?: string
  taxOffice?: string
  taxNo?: string
  taxRegistrationDate?: string
  legallyRetiredforTaxPurpose?: boolean
  foreignIncome?: boolean
  homeNumber?: string
  workNumber?: string
  faxNumber?: string
  cellNumber?: string
  emailAddress?: string
  dateJoinedGroup?: string
  probationPeriodEndDate?: string
  jobGradeCode?: string
  jobTitleTypeCode?: string
  terminationDate?: string
  uifStartDate?: string
  leaveStartDate?: string
  medicalStartDate?: string
  annualSalary?: number
  periodSalary?: number
  ratePerDay?: number
  ratePerHour?: number
  overrideAnnualBonusCalcRecurrence?: boolean
  calendarMonth?: number
  addresses?: Sage300AddressPayload[]
  bankAccounts?: Sage300BankAccountPayload[]
}

export interface Sage300AddressPayload {
  useWork: boolean
  usePhysical: boolean
  usePostal: boolean
  unitPostalNumber?: string
  streetNumber?: string
  streetFarmName?: string
  levelFloor?: string
  complex?: string
  suburbDistrict?: string
  cityTown?: string
  province?: string
  postalCode?: string
  countryCode?: string
}

export interface Sage300BankAccountPayload {
  accountName?: string
  accountNo?: string
  accountTypeCode?: string
  bankCode?: string
  bankBranchCode?: string
  defaultInd?: boolean
  status?: string
}

function normaliseDateOfBirth(dob: string | undefined | null): string {
  if (!dob) return ""
  if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) return dob
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(dob)) return dob.replace(/\//g, "-")
  const d = new Date(dob)
  if (!isNaN(d.getTime())) {
    return d.toISOString().split("T")[0]
  }
  return dob
}

interface ApiResultEnvelope {
  isSuccess: boolean
  value?: EmployeeDetailsDto
  error?: string
}

function unwrapEmployeeDetailsResult(
  data: EmployeeDetailsDto | ApiResultEnvelope,
): EmployeeDetailsDto {
  if (data && typeof data === "object" && "isSuccess" in data) {
    const envelope = data as ApiResultEnvelope
    if (!envelope.isSuccess || !envelope.value) {
      throw new Error(envelope.error || "Failed to load employee details")
    }
    return envelope.value
  }
  return data as EmployeeDetailsDto
}

function normaliseEmployeeDetails(
  data: EmployeeDetailsDto,
): EmployeeDetailsDto {
  return {
    ...data,
    dateOfBirth: normaliseDateOfBirth(data.dateOfBirth),
    address: (() => {
      if (!data.address || (data.address as unknown) === "null") return null
      if (typeof data.address === "string") {
        try {
          const p = JSON.parse(data.address)
          return {
            line1: p.Line1 ?? p.line1 ?? "",
            line2: p.Line2 ?? p.line2 ?? "",
            city: p.City ?? p.city ?? "",
            state: p.State ?? p.state ?? "",
            postalCode: p.PostalCode ?? p.postalCode ?? "",
            country: p.Country ?? p.country ?? "",
          }
        } catch {
          return null
        }
      }
      return data.address
    })(),
    bankDetails: (() => {
      if (!data.bankDetails || (data.bankDetails as unknown) === "null")
        return null
      if (typeof data.bankDetails === "string") {
        try {
          const p = JSON.parse(data.bankDetails)
          return {
            bankName: p.BankName ?? p.bankName ?? "",
            accountNumber: p.AccountNumber ?? p.accountNumber ?? "",
            branchCode: p.BranchCode ?? p.branchCode ?? "",
            accountHolderName: p.AccountHolderName ?? p.accountHolderName ?? "",
            accountType: p.AccountType ?? p.accountType ?? "",
          }
        } catch {
          return null
        }
      }
      return data.bankDetails
    })(),
  }
}

class OnboardingService {
  async getEmployees(searchTerm?: string, onboardingStatus?: string): Promise<EmployeeDto[]> {
    const response = await apiClient.get<
      Array<{ position?: string; jobTitle?: string; [key: string]: unknown }>
    >(`/Employee`, {
      params: { searchTerm, onboardingStatus },
    })
    return response.data.map((emp) => ({
      ...emp,
      jobTitle: emp.position || emp.jobTitle,
    })) as EmployeeDto[]
  }

  async getEmployeeById(id: string): Promise<EmployeeDetailsDto> {
    const response = await apiClient.get(`/employee/${id}`)
    return normaliseEmployeeDetails(unwrapEmployeeDetailsResult(response.data))
  }

  async getEmployeeDetails(id: string): Promise<EmployeeDetailsDto> {
    const response = await apiClient.get(`/employee/${id}`)
    return normaliseEmployeeDetails(unwrapEmployeeDetailsResult(response.data))
  }

  async acceptEmployee(id: string): Promise<void> {
    await apiClient.post(`/Employee/${id}/approve`)
  }

  async rejectEmployee(id: string, notes: string): Promise<void> {
    await apiClient.post(`/employee/${id}/reject`, { notes })
  }

  async createEmployee(formData: FormData): Promise<EmployeeDto> {
    const response = await apiClient.post(
      `/onboarding/onboard-employee`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    )
    return response.data.value
  }

  async updateEmployee(
    id: string,
    payload: {
      firstName: string
      lastName: string
      email: string
      jobTitle: string
      status?: EmployeeStatusDto
    },
  ): Promise<EmployeeDto> {
    const response = await apiClient.put(
      `/Employee/${id}`,
      payload.status === undefined
        ? payload
        : {
            ...payload,
            status: toEmployeeStatusCode(payload.status),
          },
    )
    return response.data
  }

  async deleteEmployee(id: string): Promise<void> {
    await apiClient.delete(`/Employee/${id}`)
  }

  async inviteEmployee(id: string): Promise<void> {
    await apiClient.post(`/Employee/${id}/send-invite`)
  }

  async downloadEmployeeDocument(
    employeeId: string,
    documentId: string,
  ): Promise<{ downloadUrl: string; fileName: string; contentType: string }> {
    const response = await apiClient.get(
      `/Employee/${employeeId}/documents/${documentId}/download`,
    )
    return response.data
  }

  async flagEmployeeSection(
    onboardingId: string,
    sectionName: string,
    note: string,
  ): Promise<void> {
    await apiClient.post(`/onboarding/${onboardingId}/flags`, {
      fieldFlags: [
        {
          fieldName: sectionName,
          note,
        },
      ],
    })
  }

  async flagEmployeeSections(
    onboardingId: string,
    flags: { fieldName: string; note: string }[],
  ): Promise<void> {
    await apiClient.post(`/onboarding/${onboardingId}/flags`, {
      fieldFlags: flags,
    })
  }

  async submitEmployeeToSage300(
    employeeId: string,
  ): Promise<{ success: boolean; message: string; entityCode?: string }> {
    const response = await apiClient.post(`/Sage300/submit/${employeeId}`)
    return response.data
  }

  async submitEmployeeDirectToSage300(
    payload: Sage300EmployeePayload,
  ): Promise<{ success: boolean; message: string; entityCode?: string }> {
    const response = await apiClient.post(`/Sage300/submit-direct`, payload)
    return response.data
  }

  async exportToSageVip(
    organizationId: string,
    vipCompanyCode: string,
    fileName: string,
    employees: SageVipEmployeeExportData[],
  ): Promise<void> {
    const response = await apiClient.post(
      `/organizations/${organizationId}/sagevip/export`,
      { vipCompanyCode, employees },
      { responseType: "blob" },
    )
    triggerCsvDownload(response.data, `${fileName}.csv`)
  }
}

export const onboardingService = new OnboardingService()
